import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { ROLES } from "@salesboard/contracts";
import { TooltipProvider } from "./components/ui/tooltip";
import { AppLayout } from "./app/AppLayout";
import { RequireRole, RequireSession } from "./app/RequireSession";
import { coldBackgroundPath, isDetailPath, type DetailLocationState } from "./app/detail-navigation";
import { ClientIdRedirect, OpportunityIdRedirect } from "./app/DetailRedirects";
import { LoginPage } from "./features/auth/LoginPage";
import { SetPasswordPage } from "./features/auth/SetPasswordPage";
import { ForgotPasswordPage } from "./features/auth/ForgotPasswordPage";
import { ActivityPage } from "./features/activity/ActivityPage";
import { PipelinePage } from "./features/board/PipelinePage";
import { OpportunitiesPage } from "./features/opportunities/OpportunitiesPage";
import { OpportunityModal } from "./features/opportunities/OpportunityModal";
import { ClientsPage } from "./features/clients/ClientsPage";
import { ClientModal } from "./features/clients/ClientModal";
import { TeamMemberModal } from "./features/team/TeamMemberModal";
import { UsersPage } from "./features/admin/UsersPage";
import { SettingsPage } from "./features/admin/SettingsPage";

export function App(): React.JSX.Element {
  const location = useLocation();
  const state = location.state as DetailLocationState;

  /**
   * What the surface routes render against while a detail modal is open.
   *
   * Opened from inside the app, that is the location the reader came from — carried in
   * history state — so the board underneath keeps its filters and its scroll, and closing
   * the modal is a step back rather than a re-render.
   *
   * Opened cold from a pasted link there is no such state, so the record's own surface
   * stands in: a modal floating over nothing has no context, and closing it would have
   * nowhere to go.
   */
  const background =
    state?.background?.location ??
    (isDetailPath(location.pathname) ? { ...location, pathname: coldBackgroundPath(location.pathname) } : undefined);

  return (
    /* One provider for the whole application, which is what makes moving between adjacent
       tooltips instant rather than re-waiting the delay — and the reason no component mounts
       its own. 300ms: long enough that sweeping a pointer across a toolbar shows nothing,
       short enough that a deliberate hover on a meeting-room display answers. */
    <TooltipProvider delayDuration={300}>
      <Routes location={background ?? location}>
        {/* Public: reached from an emailed link, by definition without a session. */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        <Route
          element={
            <RequireSession>
              <AppLayout />
            </RequireSession>
          }
        >
          {/* Home is the board, per the book — one route hosting both views. */}
          <Route path="/" element={<PipelinePage />} />
          <Route path="/opportunities" element={<OpportunitiesPage />} />
          {/* There is no `/analytics` route. Analytics is the pipeline's summary strip and its
              Detailed view (M54) — the standalone screen was removed on 2026-08-25, and the
              catch-all below sends an old bookmark to the pipeline, which is where the
              figures now are. */}
          {/* Not role-gated: both roles read the feed, and the server's `where` clause — not
              this route — decides which audit scopes each of them is shown. */}
          <Route path="/activity" element={<ActivityPage />} />
          <Route path="/clients" element={<ClientsPage />} />

          {/* The UUID detail routes that shipped before slugs, kept as redirects so
              bookmarks and existing links do not break. */}
          <Route path="/opportunities/:id" element={<OpportunityIdRedirect />} />
          <Route path="/clients/:id" element={<ClientIdRedirect />} />

          <Route
            path="/admin/users"
            element={
              <RequireRole role={ROLES.headOfSales}>
                <UsersPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <RequireRole role={ROLES.headOfSales}>
                <SettingsPage />
              </RequireRole>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Detail routes render *over* whatever the first block just drew. Singular and
          name-carrying: /opportunity/skoda-auto-rebrand, not a UUID.

          A modal opening another replaces this block's match, which means one whole Radix
          dialog is torn down and another built. That is the render whose *duration* used to be
          visible: with router transitions on, React kept the previous modal painted until it
          finished. The fix is `useTransitions={false}` in `main.tsx` — see the comment there
          before adding a key, a spinner or a memo boundary here. */}
      {background && (
        <Routes>
          <Route
            element={
              <RequireSession>
                <DetailOutlet />
              </RequireSession>
            }
          >
            <Route path="/opportunity/:slug" element={<OpportunityModal />} />
            <Route path="/client/:slug" element={<ClientModal />} />
            <Route path="/user/:slug" element={<TeamMemberModal />} />
          </Route>
        </Routes>
      )}
    </TooltipProvider>
  );
}

/**
 * The modal routes' layout element.
 *
 * They render into a portal of their own rather than into `AppLayout`, so the layout that
 * is already on screen behind them is not mounted a second time.
 */
function DetailOutlet(): React.JSX.Element {
  return <Outlet />;
}
